import styled, { css } from '@xstyled/styled-components';
import { jumpAnimation } from '../animation';
import { TrainingState, useTrainingStore } from '~/training/state';
import { select } from '~/lib/select';

interface Props {
  variant?: 'correct' | 'wrong' | 'llamaSurvey' | 'default';
  onInput?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const selectorVisualFeedback = (s: TrainingState) =>
  select(s, 'visualFeedback');

/**
 * CAT-1357
 * - Button chrome (sides + thick bottom): grey default; green/red when graded.
 * - Double outline ONLY on :focus:
 *     unanswered → purple
 *     correct    → green
 *     wrong      → red
 */
const QUIZ_FOCUS_RING = '#9845FB';
const CHUNKY_BOTTOM_PX = 6;

/** Raised button: thin sides + thick bottom ledge (part of the border box). */
const buttonChrome = (color: string) => css`
  border-style: solid;
  border-color: ${color};
  border-width: 2px;
  border-bottom-width: ${CHUNKY_BOTTOM_PX}px;
  box-shadow: none;
`;

/** Focus-only double outline (colored → white gap → button). */
const focusOutline = (ringColor: string) => css`
  &:focus,
  &:focus-visible {
    box-shadow:
      0 0 0 2px #ffffff,
      0 0 0 4px ${ringColor};
    outline: none !important;
  }
`;

const focusRingForVariant = (variant?: Props['variant']) => {
  if (variant === 'correct') return focusOutline('var(--color-green-dk)');
  if (variant === 'wrong') return focusOutline('var(--color-red-dk)');
  return focusOutline(QUIZ_FOCUS_RING);
};

export const QuizButton = styled.buttonBox<Props>`
  position: relative;
  border-radius: md;
  padding: md xl;
  font-weight: bold;
  background-color: white;
  color: black;
  ${buttonChrome('var(--color-grey-md)')}
  ${p => focusRingForVariant(p.variant)}

  &:disabled {
    cursor: not-allowed;
    background-color: greyLight;
    > * {
      opacity: 0.5;
    }
  }
  ${p =>
    p.variant === 'llamaSurvey'
      ? css`
          color: accentDark;
          ${buttonChrome('var(--color-accent-dk)')}
        `
      : ''}
  ${p => {
    const { visualFeedback } = useTrainingStore(selectorVisualFeedback);
    const sec = visualFeedback ? '0.7s' : '0s';

    return p.variant === 'correct'
      ? css`
          color: greenDark;
          ${buttonChrome('var(--color-green-dk)')}

          &:after {
            content: '✔';
            color: white;
            position: absolute;
            width: 28px;
            height: 28px;
            top: -10px;
            right: -10px;
            display: flex;
            align-items: center;
            justify-content: center;
            animation: ${jumpAnimation('1px', '-4px', '1px')} ${sec} linear
              infinite;
            background-color: greenDark;
            border-radius: 14px;
          }
        `
      : '';
  }}
  ${p => {
    const { visualFeedback } = useTrainingStore(selectorVisualFeedback);
    const sec = visualFeedback ? '0.7s' : '0s';

    return p.variant === 'wrong'
      ? css`
          color: redDark;
          ${buttonChrome('var(--color-red-dk)')}

          &:after {
            content: '!';
            color: white;
            position: absolute;
            width: 28px;
            height: 28px;
            border-radius: 14px;
            background-color: redDark;
            top: -10px;
            right: -10px;
            display: flex;
            align-items: center;
            justify-content: center;
            animation: ${jumpAnimation('0px', '5px', '0px')} ${sec} linear
              infinite;
          }
        `
      : '';
  }}
`;

export const QuizButtonAsInput = styled.textarea<Props>`
  position: relative;
  border-radius: md;
  padding: md xl;
  font-weight: bold;
  background-color: white;
  color: black;
  ${buttonChrome('var(--color-grey-md)')}
  width: 150px;
  flex: 1;
  /* Auto-expanding multi-line field (CAT-499): looks like the old single-line
     input but wraps and grows in height instead of scrolling horizontally. */
  display: block;
  resize: none;
  overflow: hidden;
  font-family: inherit;
  font-size: inherit;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: break-word;
  ${p => focusRingForVariant(p.variant)}

  &:disabled {
    cursor: not-allowed;
    background-color: greyLight;
    > * {
      opacity: 0.5;
    }
  }
  ${p =>
    p.variant === 'llamaSurvey'
      ? css`
          color: accentDark;
          ${buttonChrome('var(--color-accent-dk)')}
        `
      : ''}
  ${p => {
    const { visualFeedback } = useTrainingStore(selectorVisualFeedback);
    const sec = visualFeedback ? '0.7s' : '0s';

    return p.variant === 'correct'
      ? css`
          color: greenDark;
          ${buttonChrome('var(--color-green-dk)')}

          &:after {
            content: '✔';
            color: white;
            position: absolute;
            width: 28px;
            height: 28px;
            top: -14px;
            right: -14px;
            display: flex;
            align-items: center;
            justify-content: center;
            animation: ${jumpAnimation('1px', '-4px', '1px')} ${sec} linear
              infinite;
            background-color: greenDark;
            border-radius: 14px;
          }
        `
      : '';
  }}
  ${p => {
    const { visualFeedback } = useTrainingStore(selectorVisualFeedback);
    const sec = visualFeedback ? '0.7s' : '0s';

    return p.variant === 'wrong'
      ? css`
          color: redDark;
          ${buttonChrome('var(--color-red-dk)')}

          &:after {
            content: '!';
            color: white;
            position: absolute;
            width: 28px;
            height: 28px;
            border-radius: 14px;
            background-color: redDark;
            top: -14px;
            right: -14px;
            display: flex;
            align-items: center;
            justify-content: center;
            animation: ${jumpAnimation('0px', '5px', '0px')} ${sec} linear
              infinite;
          }
        `
      : '';
  }}
`;
